import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { api } from "../App";
import { toast } from "sonner";

const NotificationContext = createContext(null);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotifications must be used within NotificationProvider");
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const lastNotificationId = useRef(null);
  const pollIntervalRef = useRef(null);
  const isAuthenticatedRef = useRef(false);

  // Check if user is authenticated
  useEffect(() => {
    const token = localStorage.getItem("token");
    isAuthenticatedRef.current = !!token;
  }, []);

  // Request browser notification permission
  const requestNotificationPermission = useCallback(async () => {
    if (!("Notification" in window)) {
      console.log("This browser does not support notifications");
      return false;
    }

    if (Notification.permission === "granted") {
      return true;
    }

    if (Notification.permission !== "denied") {
      const permission = await Notification.requestPermission();
      return permission === "granted";
    }

    return false;
  }, []);

  // Show browser notification
  const showBrowserNotification = useCallback((title, body, icon = "/icons/icon-192x192.jpg") => {
    if (Notification.permission === "granted") {
      const notification = new Notification(title, {
        body,
        icon,
        badge: icon,
        vibrate: [200, 100, 200],
        tag: "lunawat-update",
        renotify: true
      });

      notification.onclick = () => {
        window.focus();
        notification.close();
        window.location.href = "/updates";
      };
    }
  }, []);

  // Show in-app toast notification
  const showInAppNotification = useCallback((title, message) => {
    toast(title, {
      description: message,
      duration: 5000,
      action: {
        label: "View",
        onClick: () => window.location.href = "/updates"
      }
    });
  }, []);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticatedRef.current) return;

    try {
      const [notifRes, countRes] = await Promise.all([
        api.get("/notifications"),
        api.get("/notifications/unread-count")
      ]);

      const newNotifications = notifRes.data;
      setNotifications(newNotifications);
      setUnreadCount(countRes.data.unread_count);

      // Check for new notifications and show alerts
      if (newNotifications.length > 0) {
        const latestNotification = newNotifications[0];
        
        // Only show notification if it's new (different from last seen)
        if (lastNotificationId.current && lastNotificationId.current !== latestNotification.id) {
          // Show both browser and in-app notification
          showBrowserNotification("LUNAWAT FOUNDATION", latestNotification.message);
          showInAppNotification(latestNotification.title, latestNotification.message);
        }
        
        lastNotificationId.current = latestNotification.id;
      }
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    }
  }, [showBrowserNotification, showInAppNotification]);

  // Mark notifications as read
  const markAsRead = useCallback(async () => {
    if (!isAuthenticatedRef.current) return;

    try {
      await api.post("/notifications/mark-read");
      setUnreadCount(0);
    } catch (error) {
      console.error("Failed to mark notifications as read:", error);
    }
  }, []);

  // Poll for new notifications
  useEffect(() => {
    const token = localStorage.getItem("token");
    isAuthenticatedRef.current = !!token;
    
    if (!token) return;

    // Request notification permission on mount
    requestNotificationPermission();

    // Initial fetch - use setTimeout to avoid synchronous setState
    const timeoutId = setTimeout(() => {
      fetchNotifications();
    }, 100);

    // Poll every 30 seconds
    pollIntervalRef.current = setInterval(fetchNotifications, 30000);

    return () => {
      clearTimeout(timeoutId);
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [fetchNotifications, requestNotificationPermission]);

  // Update auth state when token changes
  useEffect(() => {
    const handleStorageChange = () => {
      const token = localStorage.getItem("token");
      isAuthenticatedRef.current = !!token;
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const value = {
    notifications,
    unreadCount,
    fetchNotifications,
    markAsRead,
    requestNotificationPermission,
    showBrowserNotification,
    showInAppNotification
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;
